import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Card, CardContent, Chip, Divider, MenuItem, Select, Tooltip, Typography, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material';
import BarSimple from '../Charts/BarSimple';
import Sparkline from '../Charts/Sparkline';
import type { MemberFEC, FECCommittee, FECFiling, MemberFECSummary } from '../../types/fec';
import { apiService } from '../../services/apiService';
import { formatMoney, formatDateISO, deltaBadge } from '../../utils/format';

interface Props {
  bioguideId: string;
  isAdmin?: boolean;
}

const FinanceTab: React.FC<Props> = ({ bioguideId, isAdmin = false }) => {
  const [data, setData] = useState<MemberFEC | null>(null);
  const [summary, setSummary] = useState<MemberFECSummary | null>(null);
  const [showAI, setShowAI] = useState<boolean>(true);
  const [cycle, setCycle] = useState<number | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mapOpen, setMapOpen] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const [searchState, setSearchState] = useState('');
  const [searchOffice, setSearchOffice] = useState('');
  const [searchDistrict, setSearchDistrict] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);
  const [mapping, setMapping] = useState(false);
  const [reason, setReason] = useState<string>("");
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditItems, setAuditItems] = useState<any[] | null>(null);

  const [bench, setBench] = useState<any | null>(null);
  const [trends, setTrends] = useState<any | null>(null);
  const [ariaMsg, setAriaMsg] = useState<string>("");
  const [selectedCandidateId, setSelectedCandidateId] = useState<string | undefined>(undefined);

  const load = async (c?: number, cid?: string) => {
    setLoading(true);
    setError(null);
    const effectiveCid = cid !== undefined ? cid : selectedCandidateId;
    try {
      const [fecResp, sumResp, benchResp, trendResp] = await Promise.all([
        apiService.getMemberFEC(bioguideId, c, effectiveCid),
        apiService.getMemberFECSummary(bioguideId, c, effectiveCid).catch(() => null),
        apiService.getMemberFECBenchmarks(bioguideId, c, effectiveCid).catch(() => null),
        apiService.getMemberFECTrends(bioguideId, 6, effectiveCid).catch(() => null),
      ]);
      setData(fecResp.data);
      // Initialize selection from server on first load when undefined
      if (!selectedCandidateId && fecResp?.data?.fec_candidate_id) {
        setSelectedCandidateId(String(fecResp.data.fec_candidate_id));
      }
      setSummary(sumResp && (sumResp as any).data ? (sumResp as any).data : null);
      setBench(benchResp && (benchResp as any).data ? (benchResp as any).data : null);
      setTrends(trendResp && (trendResp as any).data ? (trendResp as any).data : null);
    } catch (e: any) {
      console.error('Failed to load FEC view', e);
      setError('Finance data unavailable');
      setSummary(null);
      setBench(null);
      setTrends(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { setData(null); setCycle(undefined); load(); }, [bioguideId]);
  // Announce cycle changes for accessibility
  useEffect(() => {
    if (cycle) {
      setAriaMsg(`Finance data updated for cycle ${cycle}`);
      const t = setTimeout(() => setAriaMsg(""), 1500);
      return () => clearTimeout(t);
    }
  }, [cycle]);

  const cycles = data?.cycles || [];
  const sel = cycle ?? (cycles.length ? cycles[0] : undefined);
  const cur = data?.current || ({} as any);
  const totals = cur?.totals || {};

  const hasMapping = !!data?.fec_candidate_id;
  // Consider data available as soon as there is a mapping and a selected cycle; totals may be empty initially
  const hasData = !!(hasMapping && (sel !== undefined));

  const metricDefs: Record<string, string> = useMemo(() => ({
    'Total receipts': 'All money received by the campaign during the reporting period.',
    'Total disbursements': 'All money spent by the campaign during the reporting period.',
    'Cash on hand': 'Cash available at the end of the reporting period.',
    'Debts owed': 'Outstanding debts the campaign owes at the end of the period.'
  }), []);

  const candidateLink = hasMapping && sel ? `https://www.fec.gov/data/candidate/${data!.fec_candidate_id}/?cycle=${sel}` : null;

  return (
    <Card variant="outlined" sx={{ mt: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Typography variant="h6" sx={{ mr: 'auto' }}>Campaign Finance</Typography>
          {/* aria-live region for announcing cycle changes */}
          <span aria-live="polite" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(1px, 1px, 1px, 1px)' }}>{ariaMsg}</span>
          {hasMapping && data?.fec_candidate_id && (
            <Chip size="small" color="default" label={`Mapped: ${data.fec_candidate_id}`} onClick={() => { if (candidateLink) window.open(candidateLink, '_blank'); }} sx={{ cursor: candidateLink ? 'pointer' : 'default' }} />
          )}
          {/* When multiple mappings exist (e.g., House and Senate runs), allow selecting which candidate_id to view */}
          {Array.isArray((data as any)?.mappings) && (data as any).mappings.length > 1 && (
            <Select
              size="small"
              value={selectedCandidateId || data!.fec_candidate_id || ''}
              onChange={(e:any)=>{ const cid = String(e.target.value); setSelectedCandidateId(cid); load(sel, cid); }}
              aria-label="Select FEC candidate mapping"
            >
              {(data as any).mappings.map((m:any) => {
                const lab = `${m.fec_candidate_id}${m.office ? ` (${m.office}${m.state ? `-${m.state}`:''}${m.district ? `-${m.district}`:''})` : ''}`;
                return <MenuItem key={m.fec_candidate_id} value={m.fec_candidate_id}>{lab}</MenuItem>;
              })}
            </Select>
          )}
          {hasMapping && (data as any)?.mapping?.confidence !== undefined && (data as any).mapping && typeof (data as any).mapping.confidence === 'number' && (data as any).mapping.confidence < 0.70 && (
            <Chip size="small" color="warning" variant="outlined" label={`Verify mapping (confidence ${(Math.round(((data as any).mapping.confidence as number)*1000)/10)}%)`} />
          )}
          {!hasMapping && (
            <Chip size="small" color="default" label="No FEC candidate linked" />
          )}
          {cycles.length > 0 && (
            <Select size="small" aria-label="Select FEC cycle" value={sel} onChange={(e:any)=>{ const c = Number(e.target.value); setCycle(c); load(c, selectedCandidateId); }}>
              {cycles.map((c:number) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </Select>
          )}
          {isAdmin && (
            <>
              <Button size="small" variant="outlined" aria-label="Refresh FEC data" onClick={async()=>{ try { const resp = await apiService.refreshMemberFEC(bioguideId); setAriaMsg('Finance refresh queued'); setTimeout(() => setAriaMsg(''), 1500); // gentle reload after a short delay
                              setTimeout(() => load(sel), 1800);
                            } catch (e) { setAriaMsg('Refresh requested'); setTimeout(() => setAriaMsg(''), 1200); } }}>Refresh</Button>
              <Button size="small" variant="text" onClick={()=>setMapOpen(true)} aria-label="Edit FEC mapping">{hasMapping ? 'Change' : 'Map'}</Button>
            </>
          )}
        </Box>

        {loading && <Typography variant="body2">Loading finance…</Typography>}
        {error && <Typography variant="body2" color="error">{error}</Typography>}

        {/* AI Summary (beta) */}
        {summary?.ai_summary && (
          <Card variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle1">AI Summary (beta)</Typography>
              <Button size="small" onClick={() => setShowAI((x) => !x)}>{showAI ? 'Hide' : 'Show'}</Button>
            </Box>
            {showAI && (
              <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>{summary.ai_summary}</Typography>
            )}
          </Card>
        )}

        {!loading && !hasMapping && (
          <Card variant="outlined" sx={{ p: 2, mb: 2, backgroundColor: (t)=>t.palette.action.hover }}>
            <Typography variant="body2">No FEC candidate linked. {isAdmin ? 'Admins can map a candidate using the “Map” button.' : 'Contact an administrator to link this member.'}</Typography>
          </Card>
        )}

        {hasData && (
          <>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 2 }}>
              {[['Total receipts', totals.total_receipts, (cur?.delta || {}).receipts, 'receipts'], ['Total disbursements', totals.total_disbursements, (cur?.delta || {}).disbursements, 'disbursements'], ['Cash on hand', totals.cash_on_hand_end_period, (cur?.delta || {}).cash, 'cash'], ['Debts owed', totals.debts_owed, (cur?.delta || {}).debts, 'debts']].map(([label, val, delt, key]: any) => {
                const series = Array.isArray(trends?.series) ? trends.series.map((s:any)=> s[key]) : [];
                return (
                  <Card key={label} variant="outlined"><CardContent>
                    <Tooltip title={metricDefs[label as string] || ''} placement="top">
                      <Typography variant="caption" color="text.secondary">{label}</Typography>
                    </Tooltip>
                    <Typography variant="h6">{formatMoney(val as number | undefined)}</Typography>
                    <Typography variant="caption" color="text.secondary">vs prior: <strong>{deltaBadge(delt as number | undefined)}</strong></Typography>
                    {series && series.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Sparkline values={series as number[]} />
                      </Box>
                    )}
                  </CardContent></Card>
                );
              })}
            </Box>

            {/* How they compare */}
            {(() => {
              const bmk = (summary as any)?.benchmarks || bench;
              const p = bmk?.percentiles || {};
              const off = bmk?.office || (bench?.office);
              const chamber = off === 'H' ? 'House' : off === 'S' ? 'Senate' : null;
              const pop = typeof bmk?.population_size === 'number' ? bmk.population_size : undefined;
              const pr = typeof p?.receipts === 'number' ? p.receipts : null;
              if (!chamber) return null;
              if (pop !== undefined && pop < 30) {
                return (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    How they compare: insufficient data in {chamber} cohort for {sel} (population {pop}).
                  </Typography>
                );
              }
              if (pr == null) return null;
              const topPct = Math.max(1, 100 - Number(pr));
              return (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  How they compare: <strong>top {topPct}%</strong> in receipts vs {chamber} ({sel}).
                </Typography>
              );
            })()}

            {candidateLink && (
              <Box sx={{ mb: 1 }}>
                <a href={candidateLink} target="_blank" rel="noreferrer">View candidate on FEC →</a>
              </Box>
            )}

            {(cur.aggregates?.by_state?.length ? true : false) ? (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>Top Contributions by State</Typography>
                <BarSimple data={cur.aggregates.by_state.slice(0,10)} xKey="bucket" yKey="amount" />
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">No aggregate data reported for {sel} yet.</Typography>
            )}

            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>Committees</Typography>
              <Box>
                <Button size="small" variant="text" component="a" href={apiService.getMemberFECCommitteesCsvUrl(bioguideId, sel)} target="_blank" rel="noreferrer">Export CSV</Button>
              </Box>
            </Box>
            <Box sx={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', fontSize: 14 }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: '#666' }}>
                    <th scope="col">ID</th><th scope="col">Name</th><th scope="col">Type</th><th scope="col">Rel.</th><th scope="col">Cash</th><th scope="col">Receipts</th><th scope="col">Disb.</th>
                  </tr>
                </thead>
                <tbody>
                  {cur.committees?.map((c:FECCommittee) => {
                    const cmLink = `https://www.fec.gov/data/committee/${c.committee_id}/?cycle=${sel}`;
                    return (
                      <tr key={c.committee_id} style={{ borderTop: '1px solid #eee' }}>
                        <td><a href={cmLink} target="_blank" rel="noreferrer">{c.committee_id}</a></td>
                        <td>{c.name}</td>
                        <td>{c.committee_type}</td>
                        <td>{c.relationship}</td>
                        <td>{formatMoney(c.cash_on_hand_end_period)}</td>
                        <td>{formatMoney(c.receipts)}</td>
                        <td>{formatMoney(c.disbursements)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Box>

            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>Recent filings</Typography>
              <Box>
                <Button size="small" variant="text" component="a" href={apiService.getMemberFECFilingsCsvUrl(bioguideId, sel)} target="_blank" rel="noreferrer">Export CSV</Button>
              </Box>
            </Box>
            {Array.isArray(cur.filings) && cur.filings.length > 0 ? (
              <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
                {cur.filings.slice(0,25).map((f:FECFiling, i:number) => (
                  <li key={i} style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #eee', padding: '8px 0' }}>
                    <div>
                      <div style={{ fontWeight: 600 }}>{f.report_type} • {f.form_type} ({f.report_year})</div>
                      <div style={{ color: '#666' }}>{f.document_description || '—'}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div>{formatDateISO(f.receipt_date)}</div>
                      {f.image_number && (
                        <a href={`https://docquery.fec.gov/cgi-bin/fecimg/?${f.image_number}`} target="_blank" rel="noreferrer">View</a>
                      )}
                    </div>
                  </li>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">No filings reported for {sel} yet.</Typography>
            )}
          </>
        )}

        {/* Admin mapping modal */}
        {isAdmin && (
          <>
          <Dialog open={mapOpen} onClose={()=>setMapOpen(false)} maxWidth="md" fullWidth>
            <DialogTitle>Edit FEC Mapping</DialogTitle>
            <DialogContent dividers>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                <TextField label="Name" value={searchQ} onChange={(e)=>setSearchQ(e.target.value)} size="small" />
                <TextField label="State" value={searchState} onChange={(e)=>setSearchState(e.target.value.toUpperCase())} size="small" inputProps={{ maxLength: 2 }} />
                <TextField label="Office (H/S)" value={searchOffice} onChange={(e)=>setSearchOffice(e.target.value.toUpperCase())} size="small" inputProps={{ maxLength: 1 }} />
                <TextField label="District" value={searchDistrict} onChange={(e)=>setSearchDistrict(e.target.value)} size="small" />
                <Button variant="outlined" onClick={async()=>{
                  setSearching(true);
                  try {
                    const resp = await apiService.fecSearchCandidates({ q: searchQ || undefined, state: searchState || undefined, office: searchOffice || undefined, district: searchDistrict || undefined, cycle: sel });
                    setSearchResults(resp.data?.items || []);
                  } catch (e) { setSearchResults([]); }
                  setSearching(false);
                }}>Search</Button>
                <Button variant="text" onClick={async()=>{
                  setAuditOpen(true);
                  try {
                    const resp = await apiService.fecGetMappingAudit(bioguideId, 20);
                    setAuditItems(resp.data?.items || []);
                  } catch (e) { setAuditItems([]); }
                }}>View mapping history</Button>
              </Box>
              <Box sx={{ mb: 2 }}>
                <TextField label="Reason (for audit)" value={reason} onChange={(e)=>setReason(e.target.value)} size="small" fullWidth multiline minRows={2} />
              </Box>
              <Box sx={{ maxHeight: 320, overflowY: 'auto' }}>
                <table style={{ width: '100%', fontSize: 14 }}>
                  <thead>
                    <tr style={{ textAlign: 'left', color: '#666' }}>
                      <th scope="col">Candidate ID</th><th scope="col">Name</th><th scope="col">Office</th><th scope="col">State</th><th scope="col">District</th><th scope="col"></th>
                    </tr>
                  </thead>
                  <tbody>
                  {searching && (
                    <tr><td colSpan={6}>Searching…</td></tr>
                  )}
                  {!searching && searchResults.length === 0 && (
                    <tr><td colSpan={6}>No results</td></tr>
                  )}
                  {!searching && searchResults.map((r:any, idx:number)=>{
                    const cid = r.candidate_id || r.id || r.candidateId;
                    return (
                      <tr key={idx} style={{ borderTop: '1px solid #eee' }}>
                        <td>{cid}</td>
                        <td>{r.name || r.candidate_name}</td>
                        <td>{r.office}</td>
                        <td>{r.state}</td>
                        <td>{r.district || r.election_district}</td>
                        <td>
                          <Button size="small" variant="contained" disabled={!cid || mapping} onClick={async()=>{
                            if (!cid) return;
                            setMapping(true);
                            try {
                              await apiService.fecMapMember(bioguideId, cid, reason || undefined);
                              await load(sel);
                              setMapOpen(false);
                              setReason("");
                            } catch (e) {}
                            setMapping(false);
                          }}>Set as canonical</Button>
                        </td>
                      </tr>
                    );
                  })}
                  </tbody>
                </table>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={()=>setMapOpen(false)}>Close</Button>
            </DialogActions>
          </Dialog>
          {/* Audit drawer */}
          <Dialog open={auditOpen} onClose={()=>setAuditOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Mapping History</DialogTitle>
            <DialogContent dividers>
              {!auditItems ? (
                <Typography variant="body2">Loading…</Typography>
              ) : auditItems.length === 0 ? (
                <Typography variant="body2">No audit entries</Typography>
              ) : (
                <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
                  {auditItems.map((a:any, i:number)=> (
                    <li key={i} style={{ borderBottom: '1px solid #eee', padding: '8px 0' }}>
                      <div style={{ fontWeight: 600 }}>{a.old_fec_id || '—'} → {a.new_fec_id || '—'}</div>
                      <div style={{ color: '#666' }}>{a.actor || 'unknown'} • {a.created_at ? new Date(a.created_at).toLocaleString() : ''}</div>
                      {a.reason && <div style={{ marginTop: 4 }}>{a.reason}</div>}
                    </li>
                  ))}
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={()=>setAuditOpen(false)}>Close</Button>
            </DialogActions>
          </Dialog>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default FinanceTab;
